import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Type } from 'lucide-react';
import { getFileUrl } from '../services/api';
import { fmtSecs } from '../services/voiceRecorder';
import '../styles/VoiceMessage.css';

const RATES = [1, 1.5, 2];
const RATE_KEY = 'voicePlaybackRate';
const TRANSCRIPT_KEY = 'voiceShowTranscript';
const PLAYED_KEY = 'voicePlayedIds';

// Отметки «прослушано» живут на устройстве: у чата нет статусов прочтения по файлам
function loadPlayed() {
  try { return new Set(JSON.parse(localStorage.getItem(PLAYED_KEY) || '[]')); } catch (e) { return new Set(); }
}

function markPlayed(id) {
  const set = loadPlayed();
  set.add(id);
  const trimmed = Array.from(set).slice(-500);
  try { localStorage.setItem(PLAYED_KEY, JSON.stringify(trimmed)); } catch (e) {}
}

export default function VoiceMessage({ message, isOwn }) {
  let data = {};
  try { data = JSON.parse(message.content) || {}; } catch (e) {}

  const file = (message.files || [])[0];
  const totalSecs = Math.max(0, Number(data.secs) || 0);
  const peaks = Array.isArray(data.peaks) && data.peaks.length ? data.peaks : null;
  const transcript = data.transcript || '';

  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [rate, setRate] = useState(() => Number(localStorage.getItem(RATE_KEY)) || 1);
  const [showTranscript, setShowTranscript] = useState(() => localStorage.getItem(TRANSCRIPT_KEY) === '1');
  const [played, setPlayed] = useState(() => !!file && loadPlayed().has(file.id));
  const audioRef = useRef(null);
  const rootRef = useRef(null);

  const duration = totalSecs || position;
  const progress = duration > 0 ? Math.min(1, position / duration) : 0;

  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Два голосовых одновременно — не надо
    document.dispatchEvent(new CustomEvent('voice:stop-others', { detail: { id: message.id } }));
    audio.playbackRate = rate;
    audio.play().catch(() => {});
  }, [message.id, rate]);

  // Останавливаемся, когда играть начал кто-то другой
  useEffect(() => {
    const onStopOthers = (e) => {
      if (e.detail?.id === message.id) return;
      const audio = audioRef.current;
      if (audio && !audio.paused) audio.pause();
    };
    document.addEventListener('voice:stop-others', onStopOthers);
    return () => document.removeEventListener('voice:stop-others', onStopOthers);
  }, [message.id]);

  // Соседнее голосовое просит себя проиграть (автопереход после окончания)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onPlayRequest = () => play();
    el.addEventListener('voice:play', onPlayRequest);
    return () => el.removeEventListener('voice:play', onPlayRequest);
  }, [play]);

  const handleEnded = () => {
    setPlaying(false);
    setPosition(0);
    // Как в WhatsApp: подряд идущие голосовые проигрываются одно за другим
    const all = Array.from(document.querySelectorAll('[data-voice-msg]'));
    const idx = all.findIndex(el => el === rootRef.current);
    const next = idx >= 0 ? all[idx + 1] : null;
    if (next) next.dispatchEvent(new CustomEvent('voice:play'));
  };

  const handlePlay = () => {
    setPlaying(true);
    if (!played && file) {
      setPlayed(true);
      markPlayed(file.id);
    }
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) play();
    else audio.pause();
  };

  const cycleRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length] || 1;
    setRate(next);
    localStorage.setItem(RATE_KEY, String(next));
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const toggleTranscript = () => {
    const next = !showTranscript;
    setShowTranscript(next);
    localStorage.setItem(TRANSCRIPT_KEY, next ? '1' : '0');
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const box = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0]?.clientX)) - box.left;
    const frac = Math.max(0, Math.min(1, x / box.width));
    audio.currentTime = frac * duration;
    setPosition(frac * duration);
  };

  if (!file) return null;

  // Без сохранённой волны рисуем ровную «гребёнку» — плеер всё равно должен читаться
  const bars = peaks || Array.from({ length: 40 }, (_, i) => 30 + ((i * 37) % 45));

  return (
    <div className={`voice-msg ${isOwn ? 'own' : ''}`} ref={rootRef} data-voice-msg>
      <button className="voice-msg-play" onClick={toggle} title={playing ? 'Пауза' : 'Слушать'}>
        {playing
          ? <Pause size={18} strokeWidth={2} fill="currentColor" />
          : <Play size={18} strokeWidth={2} fill="currentColor" />}
      </button>

      <div className="voice-msg-body">
        <div className="voice-msg-wave" onClick={seek}>
          {bars.map((h, i) => (
            <span
              key={i}
              className={`voice-msg-bar ${i / bars.length <= progress ? 'done' : ''}`}
              style={{ height: Math.max(3, Math.round((h / 99) * 24)) + 'px' }}
            />
          ))}
        </div>
        <div className="voice-msg-meta">
          <span className="voice-msg-time">{fmtSecs(playing || position ? position : duration)}</span>
          {!played && !isOwn && <span className="voice-msg-unplayed" title="Не прослушано" />}
        </div>
      </div>

      <button className="voice-msg-rate" onClick={cycleRate} title="Скорость воспроизведения">
        {rate === 1 ? '1×' : rate === 1.5 ? '1,5×' : '2×'}
      </button>

      {transcript && (
        <button
          className={`voice-msg-transcript-btn ${showTranscript ? 'active' : ''}`}
          onClick={toggleTranscript}
          title={showTranscript ? 'Скрыть расшифровку' : 'Показать расшифровку'}
        >
          <Type size={16} strokeWidth={2} />
        </button>
      )}

      {transcript && showTranscript && (
        <div className="voice-msg-transcript">{transcript}</div>
      )}

      <audio
        ref={audioRef}
        src={getFileUrl(file.id)}
        preload="metadata"
        onPlay={handlePlay}
        onPause={() => setPlaying(false)}
        onEnded={handleEnded}
        onTimeUpdate={(e) => setPosition(e.target.currentTime)}
      />
    </div>
  );
}
